/**
 * Contas financeiras — aba "Contas" de `TelaFinanceiro.tsx` (migração 0023).
 *
 * O pedido do dono do produto foi direto: "no financeiro precisamos de uma
 * conta para termos o saldo, não precisamos?". Bling, Tiny e Omie resolvem do
 * mesmo jeito — saldo inicial configurado uma vez, saldo atual = inicial + Σ
 * baixas — e é esse o desenho aqui. A decisão de reaproveitar a baixa de
 * `conta_receber`/`conta_pagar` como fonte do saldo, em vez de uma tabela de
 * movimento nova, está em `docs/adr/0003-saldo-de-conta-financeira.md`
 * (INV-11).
 *
 * `saldo_atual_centavos` é CACHE do servidor — esta tela nunca soma baixa
 * nenhuma, só EXIBE o que a API já calculou e, no cartão do topo, SOMA os
 * saldos das contas ativas (soma de inteiros é o que CONSTITUICAO.md §1
 * permite ao front).
 *
 * O front não autoriza nada (CONSTITUICAO.md §4.1): quem recusa criar ou
 * editar é o servidor — a trava do saldo inicial depois da primeira baixa
 * chega como 409 e a tela mostra o texto pronto.
 */
import { useEffect, useState, type FormEvent } from 'react'

import { Botao } from '../../componentes/Botao'
import { Campo, Chave } from '../../componentes/Campos'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { CartaoKPI } from '../../componentes/Listagem'
import { Icone } from '../../componentes/Icone'
import { Selo } from '../../componentes/Selo'
import { mostrarAviso } from '../../componentes/avisos'
import { ErroApi } from '../../plataforma/api'
import { analisar, formatar, formatarMoeda } from '../../plataforma/dinheiro'
import {
  carregarContasFinanceirasCompletas,
  criarContaFinanceira,
  editarContaFinanceira,
  type ContaFinanceiraCompleta,
} from '../../plataforma/financeiro'

const ROTULO_TIPO: Record<'caixa' | 'banco', string> = { caixa: 'Caixa', banco: 'Banco' }

export function PainelContas() {
  const [lista, setLista] = useState<ContaFinanceiraCompleta[] | null>(null)
  const [erro, setErro] = useState('')
  const [versao, setVersao] = useState(0)

  const [gavetaAberta, setGavetaAberta] = useState(false)
  const [editando, setEditando] = useState<ContaFinanceiraCompleta | null>(null)

  useEffect(() => {
    const controle = new AbortController()
    carregarContasFinanceirasCompletas(controle.signal)
      .then((dados) => {
        setLista(dados.contas)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(
          causa instanceof ErroApi
            ? causa.message
            : 'Não foi possível carregar as contas financeiras.',
        )
      })
    return () => controle.abort()
  }, [versao])

  function recarregar(mensagem?: string) {
    setVersao((n) => n + 1)
    if (mensagem) mostrarAviso(mensagem)
  }

  if (erro) {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto"
      >
        <Icone nome="error" tamanho={18} className="shrink-0" />
        <span className="min-w-0 flex-1">{erro}</span>
        <button
          type="button"
          onClick={() => setVersao((n) => n + 1)}
          className="h-8 shrink-0 rounded-sistema border border-erro bg-superficie px-3 text-[12.5px] font-semibold text-erro"
        >
          Tentar de novo
        </button>
      </div>
    )
  }

  if (!lista) {
    return <div className="h-[280px] w-full animate-pulse rounded-sistema bg-superficie" />
  }

  const ativas = lista.filter((c) => c.ativa)
  // Soma de inteiros — o que §1 permite ao front. O servidor já garante que
  // cada saldo_atual_centavos bate com inicial + baixas (INV-11); aqui só se
  // soma o que ele devolveu.
  const saldoTotal = ativas.reduce((total, c) => total + c.saldo_atual_centavos, 0)

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <CartaoKPI
          icone="payments"
          rotulo="Saldo em caixa e banco"
          valor={formatarMoeda(saldoTotal)}
          apoio={`${ativas.length} ${ativas.length === 1 ? 'conta ativa' : 'contas ativas'}`}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <p className="text-[13px] text-texto-suave">
          {lista.length} {lista.length === 1 ? 'conta cadastrada' : 'contas cadastradas'}
        </p>
        <Botao icone="add" onClick={() => setGavetaAberta(true)}>
          Nova conta
        </Botao>
      </div>

      {lista.length === 0 ? (
        <div className="flex flex-col items-center gap-2.5 rounded-sistema border border-borda bg-superficie px-4 py-14 text-center">
          <Icone nome="payments" tamanho={26} className="text-inativo" />
          <p className="text-sm font-semibold text-tinta">Nenhuma conta financeira ainda</p>
          <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
            Cadastre o caixa da loja ou uma conta de banco para acompanhar o saldo.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <caption className="sr-only">Contas financeiras da loja</caption>
              <thead>
                <tr className="bg-fundo">
                  {['Conta', 'Tipo', 'Saldo inicial', 'Saldo atual', 'Situação', ''].map(
                    (titulo, i) => (
                      <th
                        key={titulo || 'acoes'}
                        scope="col"
                        className={`border-b border-borda py-2.5 text-left text-[12.5px] font-semibold text-texto-suave ${
                          i === 0 ? 'pr-3 pl-4' : 'px-3'
                        } ${i === 2 || i === 3 ? 'text-right' : ''}`}
                      >
                        {titulo}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {lista.map((conta, i) => (
                  <LinhaDaConta
                    key={conta.codigo}
                    conta={conta}
                    ultima={i === lista.length - 1}
                    aoEditar={() => setEditando(conta)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {gavetaAberta && (
        <GavetaNovaConta
          aoFechar={() => setGavetaAberta(false)}
          aoConcluir={() => {
            setGavetaAberta(false)
            recarregar('Conta financeira criada.')
          }}
        />
      )}

      {editando && (
        <GavetaEditarConta
          conta={editando}
          aoFechar={() => setEditando(null)}
          aoConcluir={() => {
            setEditando(null)
            recarregar('Conta financeira atualizada.')
          }}
        />
      )}
    </div>
  )
}

function LinhaDaConta({
  conta,
  ultima,
  aoEditar,
}: {
  conta: ContaFinanceiraCompleta
  ultima: boolean
  aoEditar: () => void
}) {
  const borda = ultima ? '' : 'border-b border-divisoria'

  return (
    <tr>
      <td className={`py-2.5 pr-3 pl-4 ${borda}`}>
        <div className="text-[13.5px] font-semibold text-tinta">{conta.nome}</div>
        <div className="text-[12.5px] text-texto-suave">{conta.codigo}</div>
      </td>
      <td className={`px-3 py-2.5 text-[13px] text-texto ${borda}`}>
        <Selo tom="neutro">{ROTULO_TIPO[conta.tipo]}</Selo>
      </td>
      <td className={`tabular px-3 py-2.5 text-right text-[13px] text-texto-suave ${borda}`}>
        {formatarMoeda(conta.saldo_inicial_centavos)}
      </td>
      <td className={`tabular px-3 py-2.5 text-right text-[13.5px] font-semibold text-tinta ${borda}`}>
        {formatarMoeda(conta.saldo_atual_centavos)}
      </td>
      <td className={`px-3 py-2.5 ${borda}`}>
        <Selo tom={conta.ativa ? 'ok' : 'neutro'}>{conta.ativa ? 'Ativa' : 'Inativa'}</Selo>
      </td>
      <td className={`px-3 py-2.5 text-right ${borda}`}>
        <button
          type="button"
          onClick={aoEditar}
          className="inline-flex h-8 items-center gap-1.5 rounded-sistema border border-borda bg-superficie px-3 text-[12.5px] font-semibold text-texto hover:bg-divisoria"
        >
          <Icone nome="edit_note" tamanho={15} />
          Editar
        </button>
      </td>
    </tr>
  )
}

/* ------------------------------------------------------------- criação */

function GavetaNovaConta({
  aoFechar,
  aoConcluir,
}: {
  aoFechar: () => void
  aoConcluir: () => void
}) {
  const [codigo, setCodigo] = useState('')
  const [nome, setNome] = useState('')
  const [tipo, setTipo] = useState<'caixa' | 'banco'>('caixa')
  const [saldoInicial, setSaldoInicial] = useState('0,00')
  const [erros, setErros] = useState<Record<string, string>>({})
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    setErros({})
    setAviso('')

    const saldoCentavos = analisar(saldoInicial)
    if (saldoCentavos === null) {
      setErros({ saldo_inicial_centavos: 'Informe um valor válido.' })
      return
    }

    setSalvando(true)
    try {
      await criarContaFinanceira({
        codigo,
        nome,
        tipo,
        saldo_inicial_centavos: saldoCentavos,
      })
      aoConcluir()
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível criar a conta financeira.')
        return
      }
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Gaveta rotulo="Nova conta financeira" largura="formulario" aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo="Nova conta financeira"
        subtitulo="Caixa ou banco — o saldo atual nasce igual ao inicial"
        aoFechar={aoFechar}
      />

      <CorpoDaGaveta>
        <form onSubmit={(evento) => void salvar(evento)} noValidate className="flex flex-col gap-4">
          {aviso && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
              {aviso}
            </div>
          )}

          <Campo
            rotulo="Código *"
            value={codigo}
            onChange={(evento) => setCodigo(evento.target.value.toUpperCase())}
            erro={erros.codigo}
            placeholder="CX01"
            dica="Letras, números e hífen. É como esta conta aparece no select do lançamento."
            maxLength={40}
            required
          />

          <Campo
            rotulo="Nome *"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            erro={erros.nome}
            placeholder="Caixa da loja"
            maxLength={80}
            required
          />

          <div>
            <label htmlFor="nova-conta-tipo" className="mb-1.5 block text-[13px] font-semibold text-texto">
              Tipo
            </label>
            <select
              id="nova-conta-tipo"
              value={tipo}
              onChange={(evento) => setTipo(evento.target.value as 'caixa' | 'banco')}
              aria-invalid={erros.tipo ? true : undefined}
              className="campo"
            >
              <option value="caixa">Caixa</option>
              <option value="banco">Banco</option>
            </select>
          </div>

          <Campo
            rotulo="Saldo inicial"
            value={saldoInicial}
            onChange={(evento) => setSaldoInicial(evento.target.value)}
            erro={erros.saldo_inicial_centavos}
            inputMode="decimal"
            placeholder="0,00"
            dica="O que já existe nesta conta hoje. Trava para edição depois do primeiro título baixado nela."
          />

          <div className="flex justify-end">
            <Botao type="submit" icone="add" disabled={salvando}>
              {salvando ? 'Criando…' : 'Criar conta'}
            </Botao>
          </div>
        </form>
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}

/* --------------------------------------------------------------- edição */

function GavetaEditarConta({
  conta,
  aoFechar,
  aoConcluir,
}: {
  conta: ContaFinanceiraCompleta
  aoFechar: () => void
  aoConcluir: () => void
}) {
  const [nome, setNome] = useState(conta.nome)
  const [ativa, setAtiva] = useState(conta.ativa)
  const [saldoInicial, setSaldoInicial] = useState(formatar(conta.saldo_inicial_centavos))
  const [erros, setErros] = useState<Record<string, string>>({})
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function salvar(evento: FormEvent) {
    evento.preventDefault()
    setErros({})
    setAviso('')

    const saldoCentavos = analisar(saldoInicial)
    if (saldoCentavos === null) {
      setErros({ saldo_inicial_centavos: 'Informe um valor válido.' })
      return
    }

    setSalvando(true)
    try {
      await editarContaFinanceira(conta.codigo, {
        nome,
        ativa,
        saldo_inicial_centavos: saldoCentavos,
      })
      aoConcluir()
    } catch (causa: unknown) {
      if (!(causa instanceof ErroApi)) {
        setAviso('Não foi possível salvar a conta financeira.')
        return
      }
      setErros(causa.campos)
      setAviso(causa.message)
    } finally {
      setSalvando(false)
    }
  }

  return (
    <Gaveta rotulo={`Editar ${conta.nome}`} largura="formulario" aoFechar={aoFechar}>
      <CabecalhoDaGaveta titulo={`Editar ${conta.nome}`} subtitulo={conta.codigo} aoFechar={aoFechar} />

      <CorpoDaGaveta>
        <form onSubmit={(evento) => void salvar(evento)} noValidate className="flex flex-col gap-4">
          {aviso && (
            <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-[13px] font-medium text-erro-texto">
              {aviso}
            </div>
          )}

          <Campo
            rotulo="Nome *"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            erro={erros.nome}
            maxLength={80}
            required
          />

          <Campo
            rotulo="Saldo inicial"
            value={saldoInicial}
            onChange={(evento) => setSaldoInicial(evento.target.value)}
            erro={erros.saldo_inicial_centavos}
            inputMode="decimal"
            disabled={!conta.saldo_inicial_editavel}
            dica={
              conta.saldo_inicial_editavel
                ? 'Trava para edição depois do primeiro título baixado nesta conta.'
                : 'Travado: esta conta já tem título baixado nela. O saldo inicial não reflete mais o histórico se mudar agora.'
            }
          />

          <Chave
            ligada={ativa}
            aoMudar={setAtiva}
            rotulo="Conta ativa"
            ajuda="Contas inativas somem do select do lançamento, mas continuam no histórico."
          />

          <div className="flex justify-end">
            <Botao type="submit" disabled={salvando}>
              {salvando ? 'Salvando…' : 'Salvar alterações'}
            </Botao>
          </div>
        </form>
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar}>
          Cancelar
        </Botao>
      </RodapeDaGaveta>
    </Gaveta>
  )
}
